import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav className="fixed top-0 left-0 w-full z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
      <div className="px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
            <span className="text-lg md:text-xl font-heading font-extrabold text-foreground-950 tracking-tight whitespace-nowrap">
              Kakobuy <span className="text-primary-500">Spreadsheet</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-4" ref={dropdownRef}>
            <Link
              to="/"
              className="px-4 py-2 rounded-full text-foreground-800 text-sm font-label hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Home
            </Link>

            <Link
              to="/what-is-kakobuy-spreadsheet"
              className="px-4 py-2 rounded-full text-foreground-800 text-sm font-label hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Shopping Guide
            </Link>

            <div className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 hover:bg-background-200 text-foreground-800 text-sm font-label transition-colors cursor-pointer whitespace-nowrap"
              >
                Browse Categories
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`}></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-64 bg-background-50 rounded-lg border border-background-200/70 py-2 max-h-80 overflow-y-auto shadow-sm">
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <span className="w-7 h-7 rounded-md bg-background-100 flex items-center justify-center shrink-0">
                        <i className="ri-price-tag-3-line text-xs text-foreground-500"></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-label font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-800 cursor-pointer"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <i className={`text-xl ${isMobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-background-200/60 py-4 flex flex-col gap-1 max-h-[70vh] overflow-y-auto">
            <Link
              to="/"
              className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-md transition-colors cursor-pointer"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                <i className="ri-home-4-line text-primary-500"></i>
              </span>
              Home
            </Link>

            <Link
              to="/what-is-kakobuy-spreadsheet"
              className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-md transition-colors cursor-pointer"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center shrink-0">
                <i className="ri-book-open-line text-accent-500"></i>
              </span>
              Shopping Guide
            </Link>

            <div className="border-t border-background-200/60 my-2"></div>
            <p className="text-xs font-label text-foreground-500 uppercase tracking-wider px-3 mb-1">Browse Categories</p>
            {categories.map((cat) => (
              <a
                key={cat.slug}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="w-8 h-8 rounded-full bg-background-200 flex items-center justify-center shrink-0">
                  <i className="ri-price-tag-3-line text-xs text-foreground-500"></i>
                </span>
                {cat.name}
              </a>
            ))}

            <div className="border-t border-background-200/60 my-2"></div>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 px-3 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-full mt-2 text-center justify-center cursor-pointer"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <i className="ri-shopping-bag-line"></i>
              Buy It Now
            </a>
          </div>
        )}
      </div>
    </nav>
  );
}